import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink } from 'lucide-react';
import { CheckList, Reveal } from '@/components/page/PageKit';
import { cn } from '@/lib/utils';

export type VendorReview = {
  id: string;
  name: string;
  website: string;
  /** Short label shown as a chip, e.g. "AI screening and interviews". */
  category: string;
  bestFor: string;
  summary: string;
  strengths: string[];
  watchOuts: string[];
  pricing: string;
  /** Extra label/value lines under the summary, e.g. [['Integrations', 'Bullhorn Marketplace']]. */
  details?: [string, string][];
  /** Our own comparison page for this vendor, when one exists. */
  comparisonHref?: string;
  /** Our pricing guide for this vendor, when one exists. */
  pricingHref?: string;
  isBeatview?: boolean;
};

/** Numbered vendor write-ups. Beatview's card is dark and labelled "Our product". */
export function VendorReviews({ vendors, strengthsLabel = 'Strengths' }: { vendors: VendorReview[]; strengthsLabel?: string }) {
  return (
    <ol className="mt-12 space-y-5">
      {vendors.map((v, i) => {
        const dark = v.isBeatview;
        return (
          <Reveal key={v.id} delay={0.04}>
            <li id={`tool-${v.id}`} className="scroll-mt-28">
              <article aria-labelledby={`tool-${v.id}-name`} className={cn('rounded-3xl p-6 sm:p-8', dark ? 'bg-bv-ink text-white shadow-bv-deep' : 'border border-bv-blue-line bg-white')}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h3 id={`tool-${v.id}-name`} className={cn('font-display text-2xl font-semibold', dark ? 'text-white' : 'text-bv-ink')}>{i + 1}. {v.name}</h3>
                  <div className="flex flex-wrap gap-2">
                    {dark && <span className="bv-btn-gradient rounded-full px-3 py-1 text-xs font-semibold text-white">Our product</span>}
                    <span className={cn('rounded-full px-3 py-1 text-xs font-semibold', dark ? 'bg-white/10 text-white' : 'bg-bv-blue-wash text-bv-ink2')}>{v.category}</span>
                  </div>
                </div>
                <p className={cn('mt-3 text-[15px] font-semibold leading-6', dark ? 'text-white' : 'text-bv-ink')}>Best for: <span className={cn('font-normal', dark ? 'text-white/75' : 'text-bv-ink2')}>{v.bestFor}</span></p>
                <p className={cn('mt-3 text-[16px] leading-7', dark ? 'text-white/75' : 'text-bv-ink2')}>{v.summary}</p>
                {v.details && (
                  <dl className="mt-4 grid gap-x-8 gap-y-2 text-[15px] leading-6 sm:grid-cols-2">
                    {v.details.map(([term, value]) => (
                      <div key={term}><dt className={cn('inline font-semibold', dark ? 'text-white' : 'text-bv-ink')}>{term}: </dt><dd className={cn('inline', dark ? 'text-white/75' : 'text-bv-ink2')}>{value}</dd></div>
                    ))}
                  </dl>
                )}
                <div className="mt-6 grid gap-6 md:grid-cols-2">
                  <div>
                    <h4 className={cn('text-sm font-semibold uppercase tracking-wider', dark ? 'text-white/60' : 'text-bv-mute')}>{strengthsLabel}</h4>
                    <CheckList tone={dark ? 'dark' : 'light'} className="mt-3" items={v.strengths} />
                  </div>
                  <div>
                    <h4 className={cn('text-sm font-semibold uppercase tracking-wider', dark ? 'text-white/60' : 'text-bv-mute')}>Check before buying</h4>
                    <CheckList tone={dark ? 'dark' : 'light'} icon="minus" className="mt-3" items={v.watchOuts} />
                  </div>
                </div>
                <div className={cn('mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 border-t pt-5 text-[15px]', dark ? 'border-white/10' : 'border-bv-blue-line')}>
                  <p className={dark ? 'text-white/75' : 'text-bv-ink2'}><span className={cn('font-semibold', dark ? 'text-white' : 'text-bv-ink')}>Pricing:</span> {v.pricing}</p>
                  <div className="ml-auto flex flex-wrap gap-4 font-semibold">
                    {v.pricingHref && <Link to={v.pricingHref} className="inline-flex items-center gap-1 text-bv-blue hover:underline">{v.name} pricing <ArrowRight className="h-3.5 w-3.5" /></Link>}
                    {v.comparisonHref && <Link to={v.comparisonHref} className="inline-flex items-center gap-1 text-bv-blue hover:underline">{v.name} alternatives <ArrowRight className="h-3.5 w-3.5" /></Link>}
                    {dark
                      ? <Link to="/pricing" className="inline-flex items-center gap-1 text-white hover:underline">See Beatview plans <ArrowRight className="h-3.5 w-3.5" /></Link>
                      : <a href={v.website} target="_blank" rel="noopener noreferrer nofollow" className="inline-flex items-center gap-1 text-bv-ink hover:underline">Visit {v.name} <ExternalLink className="h-3.5 w-3.5" /></a>}
                  </div>
                </div>
              </article>
            </li>
          </Reveal>
        );
      })}
    </ol>
  );
}

/** Source list: every vendor page read, grouped under the vendor name. */
export function SourceList({ groups, reviewedLabel }: { groups: { name: string; sources: { label: string; url: string }[] }[]; reviewedLabel: string }) {
  return (
    <>
      <ul className="mt-8 divide-y divide-bv-blue-line rounded-3xl border border-bv-blue-line bg-white">
        {groups.map(group => (
          <li key={group.name} className="p-5 sm:p-6">
            <p className="font-display text-lg font-semibold text-bv-ink">{group.name}</p>
            <ul className="mt-2 space-y-1.5">
              {group.sources.map(source => (
                <li key={source.url}><a href={source.url} target="_blank" rel="noopener noreferrer nofollow" className="inline-flex max-w-full items-center gap-1 break-all text-[14px] font-medium text-bv-blue hover:underline">{source.label}<ExternalLink className="h-3 w-3 shrink-0" /></a></li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
      <p className="mt-4 text-[13px] text-bv-mute">All pages read {reviewedLabel}. Spotted something out of date? <Link to="/contact" className="font-semibold text-bv-blue hover:underline">Tell us</Link> and we’ll correct it.</p>
    </>
  );
}
